import AddIcon from '@atlaskit/icon/core/add';
import { Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { REDUCED_POPUP_OFFSET } from '@databases/atlaskit/Popup';
import FieldTypeDropdown, {
  type FieldTypeTriggerProps,
} from '@databases/field-type/FieldTypeDropdown';
import { getFieldType, FieldTypeCategory } from '@databases/fields';
import {
  DatabaseTraceAction,
  DatabaseTraceSource,
  traceDatabaseAction,
} from '@databases/frontend-metrics/perf-tracing';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { useComponent } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { getService } from '@databases/service-container';
import { SkeletonItem } from '@databases/skeleton';
import { type DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { createField, getExistingFieldNames } from '@databases/utils/database/utils';
import { getUniqueFieldName } from '@databases/utils/field-name';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, {
  type ComponentType,
  type FunctionComponent,
  type ReactElement,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

export const AddFieldButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  > * {
    width: 100%;
    flex-grow: 1;
  }
`;

export interface AddFieldButtonProps {
  contentId: string;
  currentViewId: string;
  triggerWrapper?: ComponentType;
  isDisabled?: boolean;

  showTypes?: DatabaseFieldType[];
  ensureSingleSelect?: boolean;
  customTrigger?: (props: FieldTypeTriggerProps) => ReactElement;
  placement?: 'top-start' | 'bottom-end' | 'bottom-start';
  width?: number;
  onFieldSelected?: (fieldType: DatabaseFieldType) => void;
}

/**
 * Renders a button that will add a field to a database. The database update is drafted automatically on click.
 */
const AddFieldButton: FunctionComponent<AddFieldButtonProps> = ({
  contentId,
  currentViewId,
  isDisabled,
  showTypes,
  ensureSingleSelect = false,
  customTrigger,
  placement = 'bottom-end',
  width,
  onFieldSelected,
}) => {
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const { showPanel } = usePanel();
  const client = useAnalytics();

  const { formatMessage } = useIntl();

  const database = selectDatabaseByContentIdRequired(store.getState(), {
    contentId,
  });

  const handleClick = useCallback(
    (type: DatabaseFieldType) => {
      const reliability = ReliabilityMetrics.getInstance();

      try {
        let createdField: ReturnType<typeof createField> | undefined;

        traceDatabaseAction(
          { action: DatabaseTraceAction.FieldAdd, source: DatabaseTraceSource.Table },
          () => {
            selectionsActions.deselectAll();

            const field = createField(type);
            createdField = field;
            if (ensureSingleSelect) {
              field.settings.multi = false;
            }

            const fieldLabel = formatMessage(
              messages['field-settings.field-name-inline-edit-default-text'],
              {
                defaultFieldName: formatMessage(getFieldType(field.type).label),
              }
            );
            const uniqueFieldName = getUniqueFieldName(
              fieldLabel,
              getExistingFieldNames(database, field)
            );

            dispatch(
              addChangeToDraft(contentId, {
                type: DatabaseChangeType.ADD_FIELD,
                field: { ...field, name: uniqueFieldName },
                currentViewId,
              })
            );
          }
        );
        client.sendCreateDatabaseFieldEvent({
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
          source: 'databaseCreateFieldDialog',
        });
        client.sendDatabaseFieldOptionUpdatedEvent({
          source: 'database',
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
          fieldSelection: type,
          isNewField: true,
        });

        // Call the optional callback after field creation (for context-specific analytics)
        onFieldSelected?.(type);

        reliability.success(MetricName.DATABASE_ADD_FIELD);

        if (getFieldType(type).category !== FieldTypeCategory.BASIC && createdField) {
          showPanel('FieldCreatePanel', {
            type,
            field: createdField,
          });
        }
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        reliability.failure(MetricName.DATABASE_ADD_FIELD, {
          errorMessage,
        });
        throw e;
      }
    },
    [
      onFieldSelected,
      selectionsActions,
      dispatch,
      formatMessage,
      database,
      contentId,
      currentViewId,
      client,
      showPanel,
      ensureSingleSelect,
    ]
  );

  const pressableStyles = xcss({
    width: '100%',
    height: '100%',
    color: 'color.text',
    backgroundColor: 'elevation.surface.sunken',
    ':active': {
      backgroundColor: 'elevation.surface.hovered',
      color: 'color.text.accent.blue',
    },
  });

  const visualRefreshBorderStyles = xcss({
    borderWidth: 'border.width',
    borderStyle: 'solid',
    borderColor: 'color.border',
    borderTopLeftRadius: '0',
    borderTopRightRadius: 'radius.xlarge',
    borderBottomRightRadius: '0',
    borderBottomLeftRadius: '0',
  });

  const pressableStylesClosed = xcss({
    ':hover': {
      backgroundColor: 'elevation.surface.hovered',
    },
  });

  const pressableStylesDisabled = xcss({
    color: 'color.text.subtlest',
    ':hover': {
      color: 'color.text.subtlest',
      backgroundColor: 'elevation.surface',
    },
    ':active': {
      color: 'color.text.subtlest',
      backgroundColor: 'elevation.surface',
    },
  });

  const pressableStylesOpen = xcss({
    ':hover': {
      backgroundColor: 'color.background.accent.blue.subtlest.hovered',
    },
    backgroundColor: 'elevation.surface',
    color: 'color.text.accent.blue',
  });

  const trigger = useComponent(
    // The `Pressable` component does not support an `isSelected` prpo. It gets passed through to the underlying
    // `<button>` DOM element, which results in a React warning
    ({ ref, isSelected: _isSelected, ...props }: FieldTypeTriggerProps) => (
      <SkeletonItem isButton height={20} width={20}>
        {/* @ts-ignore - wrong types coming from Atlaskit */}
        <Pressable
          testId="add-field-button"
          id="add-field-button"
          role="button"
          xcss={[
            pressableStyles,
            props['aria-expanded'] ? pressableStylesOpen : pressableStylesClosed,
            isDisabled && pressableStylesDisabled,
            visualRefreshBorderStyles,
          ]}
          ref={ref}
          isDisabled={isDisabled}
          {...props}
        >
          <AddIcon
            label={formatMessage(messages['table-layout.add-field-label'])}
            LEGACY_size="small"
          />
        </Pressable>
      </SkeletonItem>
    ),
    [
      formatMessage,
      isDisabled,
      pressableStyles,
      pressableStylesClosed,
      pressableStylesDisabled,
      pressableStylesOpen,
      visualRefreshBorderStyles,
    ]
  );

  return (
    <>
      {/* For visual refresh we are adding a border radius to table header row. Due to the way table header row is being rendered, we are able to apply border radius but
      we run into an overflow issue on scroll where the the underlying table cell borders remain visible and extend beyond the rounded corners during horizontal scrolling
      Usually an overflow: hidden will solve this problem, but because we have an extra th element at the beginning and end of the row with a position of sticky this causes unwanted issues
      To resolve, we use clip-path to hide the extended table rows for now and we can revisit if we refactor our table layout
      */}
      <>
        <div
          style={{
            width: '8px',
            height: '8px',
            backgroundColor: token('elevation.surface', '#FFFFFF'),
            position: 'absolute',
            // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
            top: '-1px',
            // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
            left: '-7px',
            right: token('space.negative.075', '-6px'),
            clipPath: `path("M 0 0 L 0 16 L 26 19 C 28 0 8 18 0 0 Z")`,
            transform: 'rotate(166deg)',
          }}
        />
        <div
          style={{
            width: '8px',
            height: '8px',
            backgroundColor: token('elevation.surface', '#FFFFFF'),
            position: 'absolute',
            bottom: '0',
            left: token('space.negative.075', '-6px'),
            clipPath: `path("M 8 0 L 8 8 L 0 8 C 1 7, 7 7, 0 8 Z")`,
          }}
        />
      </>
      <AddFieldButtonContainer>
        <FieldTypeDropdown
          trigger={customTrigger || trigger}
          onChange={handleClick}
          placement={placement}
          offset={REDUCED_POPUP_OFFSET}
          showTypes={showTypes}
          width={width}
        />
      </AddFieldButtonContainer>
    </>
  );
};

export default AddFieldButton;
